'use client';

import { useUser } from '@auth0/nextjs-auth0/client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ComponentType, useEffect } from 'react';
import { CenteredLoadingSpinner } from '@bss-portfolio-view-ui/shared-common-ui-loading-spinner';
import { useAuthProviderContext } from '../auth-provider-context';

/**
 * Higher-Order Component (HOC) for handling authentication using Auth0.
 * It wraps a given component, providing authentication checks using Auth0's `useAuth0` hook.
 * If the user is not authenticated, they are redirected to the login page.
 * If authentication is still being determined (loading), a loading message is displayed.
 *
 * @param {React.ComponentType<T>} WrappedComponent - The component to be wrapped by the HOC.
 * @returns {React.ComponentType<T>} - The wrapped component with added authentication logic.
 *
 * @template T - The type of props that WrappedComponent accepts.
 *
 * @example
 * function MyProtectedComponent() {
 *   return <div>Protected Content</div>;
 * }
 *
 * export default withAuthentication(MyProtectedComponent);
 */
export function withAuthentication<T extends object>(WrappedComponent: ComponentType<T>) {
  return function AuthenticatedComponent(props: T) {
    const { user, isLoading } = useUser();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { setReturnTo } = useAuthProviderContext();

    useEffect(() => {
      if (!isLoading && !user) {
        const searchParamsAsString = searchParams?.toString();

        setReturnTo(`${pathname}${searchParamsAsString ? `?${searchParamsAsString}` : ''}`);
        router.push('/login');
      }
    }, [user, isLoading, pathname, router, searchParams, setReturnTo]);

    if (isLoading) {
      return <CenteredLoadingSpinner />;
    }

    if (user) {
      // Render the wrapped component with all props passed down
      return <WrappedComponent {...props} />;
    }

    // Optionally, render null or a placeholder while redirecting
    return null;
  };
}
